import { Alert, Flex, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmProposedDate, proposeInterviewDates } from '@/api/interviews';
import AddProposeDate from '@/components/form/AddProposeDate';
import ConfirmProposeDate from '@/components/form/ConfirmProposeDate';
import { COMPLETED, PLANNING, WAITING_RECRUITER } from '@/constants/statuses';
import { useApiMutation } from '@/hooks/api';
import { useInterview } from '@/hooks/queries/useInterview';
import { useProposedDatesQuery } from '@/hooks/queries/useProposedDatesQuery';
import { queryKeys } from '@/shared/lib/queryKeys';
import { getRandomColor } from '@/theme/avatarPalette';
import Avatar from '@/UI/Avatar/Avatar';
import { LabelTitle, PageTitle } from '@/UI/typography';

import type { InterviewRoom } from '@/types/interview';
import type React from 'react';

interface ProposedDateProps {
  interview: InterviewRoom;
  setInterview: React.Dispatch<React.SetStateAction<InterviewRoom | null>>;
}
const ProposeDateForm = ({ interview, setInterview }: ProposedDateProps) => {
  const [t] = useTranslation();
  const isPlanning = interview?.current_stage?.status === PLANNING;
  const isUncompleted = interview?.current_stage?.tasks?.filter(
    (el) => el.status !== COMPLETED,
  ).length;
  const [activeKey, setActiveKey] = useState(isPlanning ? '2' : '1');
  useEffect(() => {
    setActiveKey(interview?.current_stage?.status === PLANNING ? '2' : '1');
  }, [interview]);
  const waitingForRecruiter =
    interview?.current_stage?.status === WAITING_RECRUITER;

  const { data: proposedDatesData, isLoading: loading } = useProposedDatesQuery(
    interview.id,
  );
  const dates = proposedDatesData?.proposed_dates ?? [];

  const { refetch: refetchInterview } = useInterview(interview.id);

  const confirmDateMutation = useApiMutation({
    mutationFn: (dateId: string) => confirmProposedDate(interview.id, dateId),
    successKey: 'DATE_CONFIRMED',
    successDuration: 3,
    invalidateKeys: [
      queryKeys.interviews.detail(interview.id),
      queryKeys.interviews.proposedDates(interview.id),
    ],
    onSuccess: async () => {
      const { data: updatedInterview } = await refetchInterview();
      if (updatedInterview) {
        setInterview(updatedInterview);
      }
    },
  });

  const proposeDatesMutation = useApiMutation({
    mutationFn: (newDates: Record<string, unknown>[]) =>
      proposeInterviewDates(interview.id, newDates),
    invalidateKeys: [
      queryKeys.interviews.detail(interview.id),
      queryKeys.interviews.proposedDates(interview.id),
    ],
    onSuccess: async () => {
      const { data: updatedInterview } = await refetchInterview();
      if (updatedInterview) {
        setInterview(updatedInterview);
      }
    },
  });

  const handleComplete = (date_id: string) => {
    confirmDateMutation.mutate(date_id);
  };

  const addDates = async (newDates: Record<string, unknown>[]) => {
    return proposeDatesMutation.mutateAsync(newDates);
  };

  return (
    <>
      <PageTitle>{t('PLAN_INTERVIEW')}</PageTitle>
      <Flex align="center" justify="center">
        <Flex align="center">
          <Avatar
            name={interview.job.company.name}
            src={
              interview.job.company.image
                ? interview.job.company.image
                : import.meta.env.BASE_URL + 'images/company-logo.png'
            }
            color={getRandomColor()}
            title={interview.job.company.name}
          />
          <Typography.Text type="secondary">&</Typography.Text>
          <Avatar
            name={interview.candidate.name}
            color={getRandomColor()}
            title={interview.candidate.name}
          />
        </Flex>
        <Flex justify="flex-end">
          <LabelTitle>{interview.job.title}</LabelTitle>
        </Flex>
      </Flex>
      {isUncompleted ? (
        <Tabs
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key)}
          items={[
            {
              key: '1',
              label: t('PROPOSED_DATES'),
              disabled: isPlanning,
              children: (
                <ConfirmProposeDate
                  dates={dates}
                  onConfirm={handleComplete}
                  interview={interview}
                  loading={loading}
                />
              ),
            },
            {
              key: '2',
              label: isPlanning ? t('DATES') : t('OTHER_DATES'),
              disabled: waitingForRecruiter,
              children: <AddProposeDate addDates={addDates} />,
            },
          ]}
        />
      ) : (
        <Alert type="error" showIcon={false} title={t('NOT_ACTUAL')} />
      )}
    </>
  );
};

export default ProposeDateForm;
